import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { ListResult } from 'pocketbase';
import type { Creator, CreatorStatus } from '@/types';

type MoveVars = {
  id: string;
  from: CreatorStatus;
  to: CreatorStatus;
  creator: Creator;
};
type PageData = InfiniteData<ListResult<Creator>>;
export type Snapshot = {
  from?: PageData | undefined;
  to?: PageData | undefined;
};

function findKey(
  qc: QueryClient,
  status: CreatorStatus
): readonly unknown[] | undefined {
  return qc.getQueriesData({ queryKey: ['pipeline', status] })[0]?.[0];
}

function removeCreator(data: PageData, id: string): PageData {
  return {
    ...data,
    pages: data.pages.map((p) => ({
      ...p,
      items: p.items.filter((l) => l.id !== id),
      totalItems: p.totalItems - (p.items.some((l) => l.id === id) ? 1 : 0),
    })),
  };
}

function prependCreator(data: PageData, creator: Creator): PageData {
  const [first, ...rest] = data.pages;
  if (!first) return data;
  return {
    ...data,
    pages: [
      {
        ...first,
        items: [creator, ...first.items],
        totalItems: first.totalItems + 1,
      },
      ...rest,
    ],
  };
}

export function applyOptimistic(qc: QueryClient, v: MoveVars): Snapshot {
  const snap: Snapshot = {};
  const fk = findKey(qc, v.from);
  const tk = findKey(qc, v.to);
  if (fk) {
    snap.from = qc.getQueryData<PageData>(fk);
    if (snap.from)
      qc.setQueryData<PageData>(fk, removeCreator(snap.from, v.id));
  }
  if (tk) {
    snap.to = qc.getQueryData<PageData>(tk);
    if (snap.to)
      qc.setQueryData<PageData>(
        tk,
        prependCreator(snap.to, { ...v.creator, status: v.to })
      );
  }
  return snap;
}

export function rollback(qc: QueryClient, v: MoveVars, ctx?: Snapshot): void {
  if (ctx?.from) {
    const k = findKey(qc, v.from);
    if (k) qc.setQueryData<PageData>(k, ctx.from);
  }
  if (ctx?.to) {
    const k = findKey(qc, v.to);
    if (k) qc.setQueryData<PageData>(k, ctx.to);
  }
}
